import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { Copy, Users, Gift, Share2, Mail } from 'lucide-react';
import { useToast } from '@/hooks/use-toast';
import { supabase } from '@/integrations/supabase/client';
import { copyToClipboard } from '@/utils/registrationLinks';

interface Referral {
  id: string;
  referral_code: string;
  referred_email: string | null;
  status: string;
  credits_earned: number;
  created_at: string;
  completed_at: string | null;
}

const ReferralPage = () => {
  const [referrals, setReferrals] = useState<Referral[]>([]);
  const [userReferralCode, setUserReferralCode] = useState<string>('');
  const [totalCredits, setTotalCredits] = useState(0);
  const [loading, setLoading] = useState(true);
  const [emailToRefer, setEmailToRefer] = useState('');
  const { toast } = useToast();

  useEffect(() => {
    loadReferralData();
  }, []);

  const loadReferralData = async () => {
    try {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) return;

      // Get user's referrals
      const { data: referralData, error } = await supabase
        .from('referrals')
        .select('*')
        .eq('referrer_user_id', user.id)
        .order('created_at', { ascending: false });

      if (error) throw error;

      setReferrals(referralData || []);
      
      // Calculate total credits
      const total = (referralData || []).reduce((sum, ref) => sum + ref.credits_earned, 0);
      setTotalCredits(total);

      // Get or create user's main referral code
      if (referralData && referralData.length > 0) {
        setUserReferralCode(referralData[0].referral_code);
      } else {
        await createNewReferralCode();
      }
    } catch (error) {
      console.error('Error loading referral data:', error);
      toast({
        title: "Error",
        description: "Failed to load referral data.",
        variant: "destructive"
      });
    } finally {
      setLoading(false);
    }
  };

  const createNewReferralCode = async () => {
    try {
      const { data, error } = await supabase.rpc('generate_referral_code');
      if (error) throw error;

      const { data: { user } } = await supabase.auth.getUser();
      if (!user) return;

      const { error: insertError } = await supabase
        .from('referrals')
        .insert({
          referrer_user_id: user.id,
          referral_code: data,
          status: 'pending'
        });

      if (insertError) throw insertError;

      setUserReferralCode(data);
    } catch (error) {
      console.error('Error creating referral code:', error);
      toast({
        title: "Error",
        description: "Failed to create referral code.",
        variant: "destructive"
      });
    }
  };

  const getReferralLink = () => {
    return `${window.location.origin}/register?ref=${userReferralCode}`;
  };

  const handleCopyLink = async () => {
    const link = getReferralLink();
    const success = await copyToClipboard(link);
    if (success) {
      toast({
        title: "Copied!",
        description: "Referral link copied to clipboard.",
      });
    } else {
      toast({
        title: "Error",
        description: "Failed to copy link to clipboard.",
        variant: "destructive"
      });
    }
  };

  const handleEmailRefer = () => {
    const subject = "Join me on ZentrixInsights!";
    const body = `Hi there!\n\nI've been using ZentrixInsights for behavioral and cognitive assessments and thought you might find it useful too.\n\nUse my referral link to get started: ${getReferralLink()}\n\nBest regards!`;
    window.open(`mailto:${emailToRefer}?subject=${encodeURIComponent(subject)}&body=${encodeURIComponent(body)}`);
    setEmailToRefer('');
  };

  const handleSocialShare = (platform: string) => {
    const link = getReferralLink();
    const text = "Check out ZentrixInsights for behavioral and cognitive assessments!";
    
    switch (platform) {
      case 'twitter':
        window.open(`https://twitter.com/intent/tweet?text=${encodeURIComponent(text)}&url=${encodeURIComponent(link)}`);
        break;
      case 'linkedin':
        window.open(`https://www.linkedin.com/sharing/share-offsite/?url=${encodeURIComponent(link)}`);
        break;
      case 'facebook':
        window.open(`https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(link)}`);
        break;
    }
  };

  if (loading) {
    return (
      <div className="container mx-auto p-6">
        <div className="text-center">Loading referral data...</div>
      </div>
    );
  }

  return (
    <div className="container mx-auto p-6 space-y-6">
      <div className="flex items-center gap-2 mb-6">
        <Gift className="h-6 w-6 text-primary" />
        <h1 className="text-3xl font-bold">Referral Program</h1>
      </div>

      {/* Stats Cards */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Total Referrals</CardTitle>
            <Users className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{referrals.length}</div>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Successful Referrals</CardTitle>
            <Gift className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">
              {referrals.filter(r => r.status === 'completed').length}
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Credits Earned</CardTitle>
            <Badge variant="secondary">{totalCredits}</Badge>
          </CardHeader>
          <CardContent>
            <div className="text-sm text-muted-foreground">
              1 credit = 1 free assessment
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Referral Link Section */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Share2 className="h-5 w-5" />
            Your Referral Link
          </CardTitle>
          <CardDescription>
            Share this link with friends and earn 1 free assessment credit for each person who completes an assessment.
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="flex gap-2">
            <Input 
              value={getReferralLink()} 
              readOnly 
              className="flex-1"
            />
            <Button onClick={handleCopyLink} variant="outline" size="icon">
              <Copy className="h-4 w-4" />
            </Button>
          </div>

          {/* Email Referral */}
          <div className="space-y-2">
            <Label htmlFor="emailRefer">Send via Email</Label>
            <div className="flex gap-2">
              <Input
                id="emailRefer"
                type="email"
                placeholder="Enter email address"
                value={emailToRefer}
                onChange={(e) => setEmailToRefer(e.target.value)}
                className="flex-1"
              />
              <Button 
                onClick={handleEmailRefer} 
                disabled={!emailToRefer}
                variant="outline"
              >
                <Mail className="h-4 w-4 mr-2" />
                Send
              </Button>
            </div>
          </div>

          {/* Social Sharing */}
          <div className="space-y-2">
            <Label>Share on Social Media</Label>
            <div className="flex gap-2">
              <Button 
                onClick={() => handleSocialShare('twitter')} 
                variant="outline" 
                size="sm"
              >
                Twitter
              </Button>
              <Button 
                onClick={() => handleSocialShare('linkedin')} 
                variant="outline" 
                size="sm"
              >
                LinkedIn
              </Button>
              <Button 
                onClick={() => handleSocialShare('facebook')} 
                variant="outline" 
                size="sm"
              >
                Facebook
              </Button>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Referral History */}
      <Card>
        <CardHeader>
          <CardTitle>Referral History</CardTitle>
          <CardDescription>
            Track your referral progress and earned credits.
          </CardDescription>
        </CardHeader>
        <CardContent>
          {referrals.length === 0 ? (
            <div className="text-center py-8 text-muted-foreground">
              No referrals yet. Start sharing your link to earn credits!
            </div>
          ) : (
            <div className="space-y-4">
              {referrals.map((referral) => (
                <div
                  key={referral.id}
                  className="flex items-center justify-between p-4 border rounded-lg"
                >
                  <div className="space-y-1">
                    <div className="font-medium">
                      Code: {referral.referral_code}
                    </div>
                    {referral.referred_email && (
                      <div className="text-sm text-muted-foreground">
                        {referral.referred_email}
                      </div>
                    )}
                    <div className="text-sm text-muted-foreground">
                      Created: {new Date(referral.created_at).toLocaleDateString()}
                      {referral.completed_at && (
                        <span className="ml-2">
                          • Completed: {new Date(referral.completed_at).toLocaleDateString()}
                        </span>
                      )}
                    </div>
                  </div>
                  <div className="flex items-center gap-2">
                    <Badge
                      variant={referral.status === 'completed' ? 'default' : 'secondary'}
                    >
                      {referral.status}
                    </Badge>
                    {referral.credits_earned > 0 && (
                      <Badge variant="outline">
                        +{referral.credits_earned} credit{referral.credits_earned !== 1 ? 's' : ''}
                      </Badge>
                    )}
                  </div>
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
};

export default ReferralPage;